import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { 
  Calendar, 
  MapPin, 
  Award, 
  FileText, 
  Camera, 
  Gift, 
  Users,
  CheckCircle2,
  Star,
  Ticket,
  Clock,
  ArrowRight,
  Sparkles,
  Crown,
  Zap,
  Share2,
  AlertTriangle,
  X
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const valueItems = [
  {
    icon: FileText,
    title: 'Income Accelerator Guide',
    value: '$497',
    description: 'How to charge $350+ for Keratin services and get re-booked every 4 months'
  },
  {
    icon: Award,
    title: 'Professional Certification',
    value: '$250',
    description: 'High-quality framed certificate to display in your salon'
  },
  {
    icon: Camera,
    title: 'Social Media Content Kit',
    value: '$350',
    description: 'Professional images to take home & use on your own social media'
  },
  {
    icon: Users,
    title: 'Priority Access - Front Row',
    value: '$400',
    description: 'Closer to the stage for the best learning experience'
  },
  {
    icon: Share2,
    title: 'Network Opportunities',
    value: '$300',
    description: 'Connect with industry professionals & grow your business'
  },
  {
    icon: Gift,
    title: 'Product Credit Voucher',
    value: '$50',
    description: 'Immediate credit toward your first Winamor Iron purchase'
  },
  {
    icon: Zap,
    title: 'Priority Founder Access',
    value: '$331',
    description: 'Exclusive photo with the founder - social proof for your business'
  }
];

// Education dates - Sundays, one per state in 2026
const educationDates = [
  { city: 'Melbourne', date: 'Sunday, March 22nd 2026', month: 'MAR', day: '22' },
  { city: 'Sydney', date: 'Sunday, May 25th 2026', month: 'MAY', day: '25' },
  { city: 'Perth', date: 'Sunday, August 3rd 2026', month: 'AUG', day: '3' },
  { city: 'Gold Coast', date: 'Sunday, October 12th 2026', month: 'OCT', day: '12' },
];

// Keratin types
const keratinTypes = [
  'Nano Keratin',
  'No Yellow for Blondes',
  'Gold Caviar Treatment',
  'Express Smoothing'
];

// Floating urgency popup component
const UrgencyPopup = ({ isVisible, onClose }) => {
  if (!isVisible) return null;
  
  return (
    <div className="fixed bottom-4 right-4 z-50 animate-bounce-slow">
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-3 rounded-xl shadow-2xl shadow-red-500/30 border border-red-400/30 max-w-[280px] relative">
        <button 
          onClick={onClose}
          className="absolute -top-2 -right-2 w-6 h-6 bg-white text-red-600 rounded-full flex items-center justify-center shadow-lg hover:bg-red-100 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <div className="font-bold text-sm">🔥 LIMITED SPOTS!</div>
            <div className="text-xs text-red-100">Only a few VIP seats left for 2026 tour</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ClassRegistration = ({ id }) => {
  const [step, setStep] = useState('free'); // 'free' or 'upsell'
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(educationDates[0]);
  const [showUrgencyPopup, setShowUrgencyPopup] = useState(false);
  const [freeTicketData, setFreeTicketData] = useState({
    first_name: '',
    email: '',
    phone: '',
    honeypot: '' // Anti-bot field
  });

  // Show urgency popup after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowUrgencyPopup(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleFreeTicket = async (e) => {
    e.preventDefault();
    
    // Bot check
    if (freeTicketData.honeypot) {
      toast.error('Invalid submission');
      return;
    }
    
    if (!freeTicketData.email || !freeTicketData.first_name || !freeTicketData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...freeTicketData,
        source: 'free_ticket',
        ticket_type: 'free_ticket',
        event_city: selectedDate.city,
        event_date: selectedDate.date
      });
      toast.success('Your FREE spot is reserved!');
      setStep('upsell');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVIPUpgrade = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create-session`, {
        product_type: 'vip_ticket',
        origin_url: window.location.origin,
        email: freeTicketData.email,
        metadata: {
          event_city: selectedDate.city,
          event_date: selectedDate.date
        }
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Unable to start registration. Please try again.');
      setLoading(false);
    }
  };

  return (
    <section id={id} className="section bg-[#0a0518] relative overflow-x-hidden">
      {/* Background effects */}
      <div className="absolute top-0 right-0 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-yellow-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] bg-purple-600/10 rounded-full blur-3xl" />
      
      <div className="container-custom relative z-10 overflow-x-hidden">
        {/* Section Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/40 rounded-full px-4 py-2 mb-6">
            <Star className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-medium text-yellow-200">2026 Education Tour</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Keratin <span className="text-yellow-400 italic">Masterclass</span>
          </h2>
          <p className="text-purple-200 text-lg max-w-2xl mx-auto">
            Learn all 4 types of Keratin treatments from Australia's leading professionals.
            Join us in Melbourne, Sydney, Perth & Gold Coast throughout 2026!
          </p>
        </div>

        {/* Date Selection */}
        <div className="mb-12 animate-fade-in-up">
          <h3 className="text-center text-white font-semibold mb-6">Select Your Preferred Date & Location:</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {educationDates.map((dateInfo, index) => (
              <button
                key={index}
                onClick={() => setSelectedDate(dateInfo)}
                className={`p-3 rounded-xl text-center transition-all ${
                  selectedDate === dateInfo 
                    ? 'bg-purple-500 border-2 border-purple-400 shadow-lg shadow-purple-500/30' 
                    : 'bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20'
                }`}
              >
                <div className={`text-xs font-bold mb-1 ${selectedDate === dateInfo ? 'text-purple-200' : 'text-purple-400'}`}>
                  {dateInfo.month}
                </div>
                <div className={`text-2xl font-bold mb-1 ${selectedDate === dateInfo ? 'text-white' : 'text-purple-200'}`}>
                  {dateInfo.day}
                </div>
                <div className={`text-xs ${selectedDate === dateInfo ? 'text-purple-200' : 'text-purple-400'}`}>
                  {dateInfo.city}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-4 sm:gap-8">
          {/* Main Card - Two Step Process */}
          <div className="lg:col-span-3 animate-fade-in-up min-w-0">
            {step === 'free' ? (
              /* Step 1: FREE Ticket */
              <div className="glass-card p-3 sm:p-4 md:p-8 border-2 border-green-500/40">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Ticket className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
                  </div>
                  <div>
                    <div className="text-green-400 font-bold uppercase text-xs sm:text-sm">Step 1 of 2</div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white">Reserve Your FREE Spot</h3>
                  </div>
                </div>

                {/* Selected Event Details */}
                <div className="bg-purple-500/10 rounded-xl p-3 sm:p-4 mb-6 overflow-hidden">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 flex-shrink-0" />
                      <span className="text-white font-semibold text-sm sm:text-base">{selectedDate.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 flex-shrink-0" />
                      <span className="text-white font-semibold text-sm sm:text-base">{selectedDate.city}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 flex-shrink-0" />
                      <span className="text-purple-300 text-sm sm:text-base">9:00 AM - 5:00 PM</span>
                    </div>
                  </div>
                </div>

                {/* What You'll Learn */}
                <div className="mb-6">
                  <h4 className="text-base sm:text-lg font-bold text-white mb-3">Master All 4 Keratin Types:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    {keratinTypes.map((type, i) => (
                      <div key={i} className="flex items-center gap-2 text-purple-200 text-sm sm:text-base">
                        <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span>{type}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* FREE Registration Form */}
                <form onSubmit={handleFreeTicket} className="space-y-4">
                  {/* Honeypot field - hidden from users, catches bots */}
                  <input
                    type="text"
                    name="website"
                    value={freeTicketData.honeypot}
                    onChange={(e) => setFreeTicketData({...freeTicketData, honeypot: e.target.value})}
                    style={{ position: 'absolute', left: '-9999px' }}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                      type="text"
                      placeholder="First Name *"
                      data-testid="free-ticket-name"
                      className="input-glass"
                      value={freeTicketData.first_name}
                      onChange={(e) => setFreeTicketData({...freeTicketData, first_name: e.target.value})}
                      required
                    />
                    <Input
                      type="email"
                      placeholder="Email Address *"
                      data-testid="free-ticket-email"
                      className="input-glass"
                      value={freeTicketData.email}
                      onChange={(e) => setFreeTicketData({...freeTicketData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Input
                      type="tel"
                      placeholder="Phone Number *"
                      data-testid="free-ticket-phone"
                      className="input-glass"
                      value={freeTicketData.phone}
                      onChange={(e) => setFreeTicketData({...freeTicketData, phone: e.target.value})}
                      required
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="claim-free-ticket-btn"
                    className="w-full py-4 text-lg font-bold flex items-center justify-center gap-2"
                    style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: 'white' }}
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Ticket className="w-5 h-5" />
                        Claim Your FREE Spot - {selectedDate.city}
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
                
                <p className="text-center text-purple-400 text-sm mt-4">
                  Limited spots available at each location. Reserve now to guarantee your seat.
                </p>
              </div>
            ) : (
              /* Step 2: VIP Upsell */
              <div className="vip-ticket p-4 sm:p-8">
                {/* Success Message */}
                <div className="bg-green-500/20 border border-green-500/40 rounded-xl p-3 sm:p-4 mb-6 flex items-start sm:items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 text-green-400 flex-shrink-0 mt-0.5 sm:mt-0" />
                  <div>
                    <div className="text-green-400 font-bold text-sm sm:text-base">Your FREE spot in {selectedDate.city} is reserved!</div>
                    <div className="text-green-300 text-xs sm:text-sm">{selectedDate.date} - Check your email for confirmation</div>
                  </div>
                </div>

                {/* VIP Upgrade Header */}
                <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                      <Crown className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <div className="text-yellow-400 font-bold uppercase text-sm">Exclusive Upgrade</div>
                      <h3 className="text-xl sm:text-2xl font-bold text-white">Upgrade to VIP Access</h3>
                    </div>
                  </div>
                  <div className="text-left sm:text-right bg-yellow-500/10 px-4 py-2 rounded-lg border border-yellow-500/30">
                    <div className="text-sm text-yellow-300 mb-1">Total Value</div>
                    <div className="text-2xl sm:text-3xl font-bold text-yellow-400 mono">$2,178</div>
                    <div className="text-xs text-yellow-300">for only $99!</div>
                  </div>
                </div>

                {/* VIP Benefits */}
                <div className="bg-purple-500/10 rounded-xl p-3 sm:p-4 mb-6">
                  <h4 className="font-bold text-white mb-3 flex items-center gap-2 text-sm sm:text-base">
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 flex-shrink-0" />
                    VIP Members Get EVERYTHING:
                  </h4>
                  <div className="grid gap-2 text-xs sm:text-sm">
                    <div className="flex items-start gap-2 text-purple-200">
                      <CheckCircle2 className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" /> <span>Income Accelerator Guide ($497 value)</span>
                    </div>
                    <div className="flex items-start gap-2 text-purple-200">
                      <CheckCircle2 className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" /> <span>Professional Certification ($250 value)</span>
                    </div>
                    <div className="flex items-start gap-2 text-purple-200">
                      <CheckCircle2 className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" /> <span>Social Media Content Kit - Images to take home & use ($350 value)</span>
                    </div>
                    <div className="flex items-start gap-2 text-purple-200">
                      <CheckCircle2 className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" /> <span>Priority Access - Closer to the Stage ($400 value)</span>
                    </div>
                    <div className="flex items-start gap-2 text-purple-200">
                      <CheckCircle2 className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" /> <span>Network Opportunities & Business Growth ($300 value)</span>
                    </div>
                    <div className="flex items-start gap-2 text-purple-200">
                      <CheckCircle2 className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" /> <span>Priority Founder Access & Photo ($331 value)</span>
                    </div>
                    <div className="flex items-start gap-2 text-purple-200">
                      <CheckCircle2 className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" /> <span>$50 Product Credit Voucher</span>
                    </div>
                  </div>
                </div>

                {/* Price & CTA */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-purple-500/30">
                  <div>
                    <div className="text-purple-300 text-sm mb-1">VIP Investment</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl sm:text-4xl font-bold text-white mono">$99</span>
                      <span className="text-purple-400 line-through">$299</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 w-full sm:w-auto">
                    <Button
                      onClick={handleVIPUpgrade}
                      disabled={loading}
                      data-testid="upgrade-vip-btn"
                      className="w-full sm:w-auto px-6 sm:px-8 py-4 text-base sm:text-lg flex items-center justify-center gap-2 group animate-pulse-glow"
                      style={{ 
                        background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)', 
                        color: 'white',
                        boxShadow: '0 0 30px rgba(220, 38, 38, 0.5)'
                      }}
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <Crown className="w-5 h-5" />
                          <span className="flex flex-col items-start leading-tight">
                            <span className="font-bold">UPGRADE TO VIP</span>
                            <span className="text-xs text-red-200">(UNLIMITED TICKETS)</span>
                          </span>
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                    <button 
                      onClick={() => toast.success('See you at the class!')}
                      className="text-purple-400 text-sm hover:text-purple-300 transition-colors text-center"
                    >
                      No thanks, I'll keep my free spot
                    </button>
                  </div>
                </div>

                {/* The Pitch */}
                <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                  <p className="text-green-300 text-sm font-medium">
                    "Pay $99 today, and walk away with the tools, the certificate, and the 
                    marketing plan to make $2,000 back in your first week."
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Value Breakdown - Side */}
          <div className="lg:col-span-2 space-y-3 sm:space-y-4 animate-fade-in-up stagger-2 min-w-0">
            <h4 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4">
              {step === 'free' ? 'VIP Access Includes:' : 'What\'s Included:'}
            </h4>
            {valueItems.map((item, index) => (
              <div 
                key={index}
                className="glass-card p-2.5 sm:p-3 md:p-4 flex items-start gap-2 sm:gap-3 md:gap-4 animate-fade-in-up"
                style={{ animationDelay: `${(index + 2) * 0.1}s` }}
              >
                <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-purple-400" />
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="flex flex-col gap-0.5 mb-0.5 sm:mb-1">
                    <h5 className="font-semibold text-white text-[11px] sm:text-xs md:text-sm truncate">{item.title}</h5>
                    <span className="text-yellow-400 font-bold mono text-[11px] sm:text-xs md:text-sm">{item.value}</span>
                  </div>
                  <p className="text-purple-300 text-[9px] sm:text-[10px] md:text-xs leading-tight line-clamp-2">{item.description}</p>
                </div>
              </div>
            ))}
            
            {/* All Locations Card */}
            <div className="glass-card p-2.5 sm:p-3 md:p-4 border border-yellow-500/30">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 flex-shrink-0" />
                <span className="font-semibold text-white text-xs sm:text-sm md:text-base">2026 Tour Locations</span>
              </div>
              <div className="grid grid-cols-2 gap-1 sm:gap-2 text-[10px] sm:text-xs md:text-sm">
                <div className="text-purple-300">• Melbourne - Mar 22</div>
                <div className="text-purple-300">• Sydney - May 25</div>
                <div className="text-purple-300">• Perth - Aug 3</div>
                <div className="text-purple-300">• Gold Coast - Oct 12</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Floating Urgency Popup */}
      <UrgencyPopup 
        isVisible={showUrgencyPopup} 
        onClose={() => setShowUrgencyPopup(false)} 
      />
    </section>
  );
};

export default ClassRegistration;
